import React from 'react';

const FACTOR_DESCRIPTIONS = {
  cost: 'Higher = more affordable / better net aid package',
  career: 'Higher = stronger employer recruiting & job placement',
  prestige: 'Higher = better national rankings & alumni network',
  classsize: 'Higher = smaller class sizes / better student-faculty ratio',
};

function getScoreColor(score) {
  if (score >= 8) return '#10b981';
  if (score >= 6) return '#3b82f6';
  if (score >= 4) return '#f59e0b';
  return '#ef4444';
}

function ScoreCell({ value, onChange, isEstimate }) {
  const color = getScoreColor(value);
  return (
    <div className="score-cell">
      <div className="score-bubble" style={{ background: color }}>
        {value}
      </div>
      <input
        type="range"
        min="1"
        max="10"
        value={value}
        onChange={e => onChange(parseInt(e.target.value))}
        className="score-slider-mini"
        style={{
          background: `linear-gradient(to right, ${color} ${(value - 1) * 11.1}%, #e5e7eb ${(value - 1) * 11.1}%)`,
        }}
      />
      {isEstimate && (
        <span className="badge badge-estimate" style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }}>
          est.
        </span>
      )}
    </div>
  );
}

export default function Step4Objective({ stepIndicator, schools, factors, scores, setScores, onBack, onNext, hpTheme = false }) {
  const getScore = (school, factorId) => scores[school]?.[factorId] ?? 5;
  const isEstimate = (school) => scores[school]?._hasEstimates === true;

  const setScore = (school, factorId, value) => {
    setScores(prev => ({
      ...prev,
      [school]: { ...prev[school], [factorId]: value },
    }));
  };

  if (factors.length === 0) {
    return (
      <div className="card">
        {stepIndicator}
        <div className="step-header">
          <h2>{hpTheme ? 'The Record Books' : 'Step 4 — Objective Data'}</h2>
          <p>{hpTheme ? 'No hard records to consult? Very well. We go straight to the verdict.' : 'No objective factors are enabled. Skipping ahead to results.'}</p>
        </div>
        <div className="step-footer">
          <button className="btn btn-secondary" onClick={onBack}>{hpTheme ? '← Back' : '← Back'}</button>
          <button className="btn btn-primary" onClick={onNext}>{hpTheme ? 'Hear My Verdict →' : 'See Results →'}</button>
        </div>
      </div>
    );
  }

  const hasAnyEstimates = schools.some(s => isEstimate(s));

  return (
    <div className="card">
      {stepIndicator}
      <div className="step-header">
        <h2>{hpTheme ? 'Let Us Consult The Facts' : 'Step 4 — Objective Data'}</h2>
        <p>
          {hpTheme
            ? 'Here lie the colder facts: cost, outcomes, reputation, class size, and the like. I have filled some in from common knowledge, but do correct me where you know better.'
            : 'Review the objective scores for each school. Pre-filled values are estimates based on general knowledge — adjust any you know to be inaccurate. All scores are normalized to a 1–10 scale.'}
        </p>
      </div>

      {hasAnyEstimates && !hpTheme && (
        <div className="notice notice-warning mb-4">
          <strong>Note:</strong> Values marked <span className="badge badge-estimate">est.</span> are rough estimates
          based on general knowledge (rankings, typical costs, student-faculty ratios). They are <em>not</em> precise
          figures. Adjust any score if you have better information.
        </div>
      )}

      <div style={{ marginBottom: '1rem' }}>
        {factors.map(f => (
          <div key={f.id} style={{ marginBottom: '0.3rem', fontSize: '0.8rem', color: '#6b7280' }}>
            <strong style={{ color: '#374151' }}>{f.name}:</strong>{' '}
            {FACTOR_DESCRIPTIONS[f.id] || f.description}
          </div>
        ))}
      </div>

      <div className="ratings-table-wrapper">
        <table className="ratings-table">
          <thead>
            <tr>
              <th>School</th>
              {factors.map(f => (
                <th key={f.id}>
                  <div>{f.name}</div>
                  <div style={{ fontWeight: 400, color: '#9ca3af', fontSize: '0.68rem', marginTop: '2px' }}>
                    {hpTheme ? 'Hat scale: 1-10' : '1–10 scale'}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {schools.map(school => (
              <tr key={school}>
                <td>
                  <div>{school}</div>
                  {isEstimate(school) && (
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px' }}>
                      {hpTheme ? 'the Hat has filled this in' : 'data pre-filled'}
                    </div>
                  )}
                </td>
                {factors.map(f => (
                  <td key={f.id}>
                    <ScoreCell
                      value={getScore(school, f.id)}
                      onChange={v => setScore(school, f.id, v)}
                      isEstimate={isEstimate(school)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="step-footer">
        <button className="btn btn-secondary" onClick={onBack}>{hpTheme ? '← Back To Instinct' : '← Back'}</button>
        <button className="btn btn-success" onClick={onNext}>
          {hpTheme ? 'Deliver The Verdict →' : 'Calculate Results →'}
        </button>
      </div>
    </div>
  );
}
